import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Pagination from '@material-ui/lab/Pagination';
import Rating from '@material-ui/lab/Rating';

// UTILITY
import { customAnalyticsEvent } from '@/utility/analytics';
import { sendParentMessage } from '@/utility/common';

// HOC
import CheckAuth from '@/hoc/CheckAuth';

// COMPONENTS
import Loader from '@/components/Loader';
import MBRatingIcon from '@/components/Icons/MBRatingIcon';

// HELP
import WriteReview from './help/WriteReview';

// STYLES
import { Container } from './styles';
import { formatDate } from '@/utility/date';

const RatingsReviews = ({
  itemRating,
  reviewWasLeft,
  reviews,
  reviewsMeta,
  onChangeReviewsPage,
  loading,
  analyticsData,
  logo,
  name,
  onSubmitReview,
}) => {
  const [t] = useTranslation();
  const [state, setState] = useState({ openDialog: false, rating: 0 });
  const { isIframe } = useSelector(({ app }) => ({
    isIframe: app.isIframe,
  }));
  const page = reviewsMeta.current_page || 1;
  const last_page = reviewsMeta.last_page || 1;

  const handleStartReview = (e, rating) => {
    customAnalyticsEvent('Clicked Leave Review Button', {
      ...analyticsData,
    });

    setState({ openDialog: true, rating });

    if (isIframe) {
      sendParentMessage({ goToTop: true });
    }
  };

  const handleClose = () => {
    setState({ openDialog: false, rating: 0 });
  };

  return (
    <Container id="ratings" className="ratings-reviews section section--grey">
      <div className="page-content">
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <div className="rating-content">
              <Typography className="rating-content__title" variant="h4">
                <Trans i18nKey="ratingsReviews.title" />
              </Typography>
              {!!itemRating && (
                <div className="ratings-content">
                  <div className="ratings-content__section">
                    <div className="ratings-content__rating-wrap">
                      <MBRatingIcon className="ratings-content__icon" fontSize="large" />
                      <Rating
                        className="ratings-content__rating"
                        value={+itemRating}
                        precision={0.2}
                        readOnly
                      />
                    </div>
                    <p className="ratings-content__mark">
                      {+itemRating}/<span>5</span>
                    </p>
                  </div>
                </div>
              )}
              {!reviewWasLeft && (
                <div className="write-review">
                  <Typography variant="h5">{t('ratingsReviews.writeReview')}</Typography>
                  <CheckAuth>
                    <Rating
                      name="def-rating"
                      className="sizeLarge write-review__rating"
                      value={state.rating}
                      onChange={handleStartReview}
                      precision={1}
                    />
                  </CheckAuth>
                </div>
              )}
            </div>
          </Grid>
          <Grid item xs={12} md={7}>
            <div className="reviews-content">
              {reviews.length > 0 && (
                <>
                  {reviews.map((item, i) => {
                    return (
                      <div className="reviews-content__wrapper" key={i}>
                        <div className="reviews-content__review review">
                          <div className="review__info">
                            <div className="review__title">
                              {!!item.fullname && (
                                <Typography className="p4 text-medium">{item.fullname}</Typography>
                              )}
                              <Rating value={item.rating} precision={0.1} readOnly />
                            </div>
                            {!!item.date && (
                              <Typography className="p4 text-color--grey">
                                {formatDate(item.date, 'LLL d y')}
                              </Typography>
                            )}
                          </div>
                          {!!item.title && (
                            <Typography className="m-b-10 p4" component="h5">
                              {item.title}
                            </Typography>
                          )}
                          {!!item.details && (
                            <Typography className="p4 text-regular">{item.details}</Typography>
                          )}
                        </div>
                        <Divider />
                      </div>
                    );
                  })}
                  {last_page > 1 && (
                    <Pagination page={page} count={last_page} onChange={onChangeReviewsPage} />
                  )}
                </>
              )}
              {reviews.length === 0 && !loading ? (
                <div className="reviews-content__empty">
                  <Typography variant="h5">{t('ratingsReviews.emptyState')}</Typography>
                </div>
              ) : null}
            </div>
          </Grid>
        </Grid>
      </div>
      <Loader active={loading} />
      <WriteReview
        name={name}
        logo={logo}
        open={state.openDialog}
        rating={state.rating}
        onSubmit={onSubmitReview}
        onClose={handleClose}
      />
    </Container>
  );
};

RatingsReviews.defaultProps = {
  reviewWasLeft: false,
  itemRating: 0,
  reviews: [],
  reviewsMeta: {},
  onChangeReviewsPage: () => {},
  onSubmitReview: () => {},
  loading: false,
  analyticsData: {},
  logo: '',
};

RatingsReviews.propTypes = {
  reviewWasLeft: PropTypes.bool,
  itemRating: PropTypes.number,
  reviews: PropTypes.array,
  reviewsMeta: PropTypes.object,
  onChangeReviewsPage: PropTypes.func,
  onSubmitReview: PropTypes.func,
  loading: PropTypes.bool,
  analyticsData: PropTypes.object,
  logo: PropTypes.string,
  name: PropTypes.string.isRequired,
};

export default React.memo(RatingsReviews);
